import FormCreateOrEditTransaction from '../components/formCreateOrEditTransaction';
import { useRequestGetTransactions, useRequestDeleteTransaction } from '@/hooks/httpRequest';
import  { DataTablePageSizeEnum, SortEnum,TransactionCategoryEnum, TransactionTypeEnum } from '@/enums';
import { broadcastService } from '@/services';
import { useQueryParams, useUserConnected } from '@/hooks';
import { useCallback, useEffect } from 'react';
import type { ITransaction } from '@/interfaces';
import { startOfMonth, endOfMonth } from 'date-fns';

const getDefaultPeriod = () => {
	const now = new Date();
	return {
		from: startOfMonth(now).toISOString(),
		to: endOfMonth(now).toISOString()
	};
};

const useTransactions = () => {
	const defaultPeriod = getDefaultPeriod();
	const {
		values: [sortType, sortBy, limit, page, type, category, periodFrom, periodTo],
		urlSearchParams,
		setValues
	} = useQueryParams(['sortType', 'sortBy', 'limit', 'page', 'type', 'category', 'periodFrom', 'periodTo'], {
		limit: DataTablePageSizeEnum._15,
		page: 1,
		sortType: SortEnum.DESC,
		sortBy: 'occurredAt',
		periodFrom: defaultPeriod.from,
		periodTo: defaultPeriod.to
	});

	useEffect(() => {
		if (!urlSearchParams.has('periodFrom') || !urlSearchParams.has('periodTo')) {
			const p = getDefaultPeriod();
			setValues({
				periodFrom: periodFrom || p.from,
				periodTo: periodTo || p.to
			});
		}
	}, [urlSearchParams, setValues, periodFrom, periodTo]);

	const parseMultiValues = useCallback((value: unknown): string[] => {
		if (Array.isArray(value)) return value.filter(Boolean);
		if (typeof value === 'string' && value) return [value];
		return [];
	}, []);

	const parsedType = parseMultiValues(type);
	const parsedCategory = parseMultiValues(category);

	const result = useRequestGetTransactions({
		sortType,
		sortBy,
		limit,
		page,
		type: parsedType as TransactionTypeEnum[],
		category: parsedCategory as TransactionCategoryEnum[],
		periodFrom,
		periodTo
	});

	const { trigger: triggerDelete } = useRequestDeleteTransaction();
	const { userConnected } = useUserConnected();

	const createTransaction = () =>
		broadcastService.modal.show({
			content: <FormCreateOrEditTransaction />,
			width: 'lg'
		});

	const seeDetails = (transaction: ITransaction) =>
		broadcastService.modal.show({
			content: <FormCreateOrEditTransaction transaction={transaction} />,
			width: 'lg'
		});

	const deleteTransaction = async (_id: string, isOwner = false) => {
		const canUseOwnerRoute = isOwner && !userConnected?.root;
		await triggerDelete({ _id, isOwner: canUseOwnerRoute });
	};

	return {
		...result,
		createTransaction,
		seeDetails,
		deleteTransaction
	};
};

export default useTransactions;
